import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'react-bootstrap';
import {
  fetchEventsByCategory,
  fetchEventsByCategoryWithHours,
  fetchEventsBySport,
  setSelectedLeague
} from 'stores/match';
import { fetchOutrightsByParentTournamentReq } from 'stores/outrights/services';
import { AWS_BUCKET_URL } from 'toolkit/constants';
import { calculateTimeParameter, isToday, sportTypeToSportId } from 'toolkit/utils.js';
import TitleHistoryBack from 'components/general/TitleHistoryBack.js';
import MatchCard from 'components/general/match_card.js';
import SportDetailCategory from 'components/SportDetailCategory.js';
import Loading from 'components/general/loading';
import TournamentCard from 'views/outrights/components/TournamentCard';

export default function SportDetailsMatches() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation('translations');
  const [lastScrollPosition, setLastScrollPosition] = useState(0);
  const { sportType } = useParams();
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get('category');
  const categoryNames = useSelector((state) => state.match.categoryNames);
  const leagueId = searchParams.get('league');
  const time = searchParams.get('time');
  const [loading, setLoading] = useState(false);
  const [fetchedEvents, setFetchedEvents] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const selectedLeague = useSelector((state) => state.match.selectedLeague);
  const [selectedMarketType, setSelectedMarketType] = useState(1);
  const [outrightEvents, setOutrightEvents] = useState([]);
  const [outrightsSelected, setOutrightsSelected] = useState(false);
  const hasOutrights = outrightEvents.length > 0;
  const containerRef = useRef(null);
  const selectLeague = useCallback((id) => {
    dispatch(setSelectedLeague(id));
  });

  const getData = async () => {
    try {
      setLoading(true);
      if (categoryId === 'all') {
        const { betEvents } = await dispatch(
          fetchEventsBySport({
            sport: sportTypeToSportId(sportType),
            dateFilter: calculateTimeParameter(time)
          })
        ).unwrap();
        setFetchedEvents(
          Object.values(betEvents)
            .map((c) => Object.values(c))
            .flat()
            .flat()
        );
      } else if (time) {
        const { betEvents } = await dispatch(
          fetchEventsByCategory({ category: categoryId, dateFilter: calculateTimeParameter(time) })
        ).unwrap();
        setFetchedEvents(Object.values(betEvents).flat());
      } else {
        const { betEvents } = await dispatch(
          fetchEventsByCategoryWithHours({ Category: categoryId, Hours: 0 })
        ).unwrap();
        setFetchedEvents(Object.values(betEvents).flat());
      }
    } catch (error) {
      // error
    }
    setLoading(false);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (typeof window.history !== 'undefined' && window.history.scrollRestoration) {
      window.history.scrollRestoration = 'manual';
    }
    getData();
  }, []);

  useEffect(() => {
    if (!loading) {
      window.scrollTo(0, 0);
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
      }
    }
  }, [loading]);

  useEffect(() => {
    if (!loading && fetchedEvents.length > 0) {
      let tempLeagueArr = [];
      if (fetchedEvents.find((e) => isToday(new Date(e.gameDate)))) {
        tempLeagueArr.push({
          leagueId: 'Today',
          leagueName: t(`today`),
          events: []
        });
      }
      fetchedEvents.forEach((event) => {
        if (isToday(new Date(event.gameDate))) {
          const todayIndex = tempLeagueArr.findIndex((l) => l.leagueId === 'Today');
          tempLeagueArr[todayIndex].events.push(event);
        }
        const { leagueId } = event;
        const leagueIndex = tempLeagueArr.findIndex((l) => l.leagueId === leagueId);
        if (leagueIndex === -1) {
          tempLeagueArr.push({
            leagueId,
            leagueName: event.league,
            events: [event]
          });
        } else {
          tempLeagueArr[leagueIndex].events.push(event);
        }
      }, []);
      tempLeagueArr = tempLeagueArr.sort((a, b) => {
        if (a.leagueId === 'Today' || b.leagueId === 'Today') return 1;
        return Number(b.leagueId.split(':')[2]) < Number(a.leagueId.split(':')[2]) ? 1 : -1;
      });
      if (tempLeagueArr.find((l) => l.leagueId === leagueId)) {
        selectLeague(leagueId);
      } else {
        selectLeague(tempLeagueArr[0].leagueId);
      }
      setLeagues(tempLeagueArr);
    }
  }, [fetchedEvents, t]);

  useEffect(() => {
    let cancelled = false;
    setOutrightEvents([]);
    setOutrightsSelected(false);
    const tournamentIds = leagues
      .map((l) => l.leagueId)
      .filter((id) => id && id !== 'Today');
    if (tournamentIds.length === 0) return undefined;
    (async () => {
      const collected = [];
      for (const id of tournamentIds) {
        try {
          const events = await fetchOutrightsByParentTournamentReq(id);
          if (cancelled) return;
          (events || []).forEach((e) => {
            if (Array.isArray(e?.markets) && e.markets.length > 0) collected.push(e);
          });
        } catch (e) {
          // ignore
        }
      }
      if (!cancelled) setOutrightEvents(collected);
    })();
    return () => {
      cancelled = true;
    };
  }, [leagues]);

  function getLeagues() {
    if (leagues.length > 0) {
      return leagues.map(({ leagueId, events, leagueName }) => (
        <SportDetailCategory
          key={leagueId}
          active={!outrightsSelected && selectedLeague === leagueId}
          onClick={() => {
            setOutrightsSelected(false);
            selectLeague(leagueId);
          }}
          text={leagueName}
          count={leagueId === 'Today' ? undefined : events.length}
          icon={
            <img
              width="25"
              height="25"
              src={`${AWS_BUCKET_URL}/Icons/star${selectedLeague === leagueId ? 'fill' : 'empty'
                }.svg`}
            />
          }
        />
      ));
    }
    return null;
  }

  function getBetType() {
    const betTypes = [
      {
        label: t('winner'),
        id: 1
      },
      {
        label: t('overunder'),
        id: 18
      },
      {
        label: t('handicap'),
        id: 14
      },
      {
        label: t('nextGoal'),
        id: 8
      },
      {
        label: t('bs'),
        id: 29
      },
      {
        label: t('doubleChance'),
        id: 10
      }
    ];
    return (
      <div className="tip-types">
        {betTypes.map((betType) => (
          <div className="d-inline-block w-25 text-align-center fw-bold" key={betType.id}>
            <button
              className={`btn btn-sm w-100 border fs-8 ${selectedMarketType === betType.id ? 'btn-primary fw-bold text-white' : 'btn-light'
                }`}
              onClick={() => setSelectedMarketType(betType.id)}>
              {betType.label}
            </button>
          </div>
        ))}
      </div>
    );
  }

  function getBets() {
    const matchCardList = [];
    const leagueArray = [];
    const selectedLeagueObj = leagues.find((l) => l.leagueId === selectedLeague);
    if (!selectedLeagueObj) return;
    const { events } = selectedLeagueObj;
    if (events.length > 0) {
      events
        .sort((a, b) => new Date(a.gameDate).getTime() - new Date(b.gameDate).getTime())
        .filter((a) => a.markets && a.league && a.gameNumber)
        .forEach(function (bet) {
          const findedMarket = bet.markets.find((market) => market.id === selectedMarketType);
          const lock =
            !findedMarket ||
            !findedMarket.outcomes ||
            findedMarket.outcomes.findIndex((element) => element.odds !== null) === -1;
          if (!leagueArray.includes(bet.league)) {
            let code = null;
            if (bet.categoryId) {
              code = bet.categoryId.split(':')[2];
            }
            leagueArray.push(bet.league);
            matchCardList.push(
              <div
                className="text-start my-1 fw-bold fs-8 d-flex align-items-center"
                key={'league-' + bet.league}>
                {code && (
                  <img
                    width="20"
                    height="13"
                    src={`${AWS_BUCKET_URL}/ClientFlags/${code}.svg`}
                    alt={code}
                    className="me-2"
                  />
                )}
                {bet.league}
              </div>
            );
          }
          return matchCardList.push(
            <MatchCard
              key={bet.gameNumber}
              marketType={selectedMarketType}
              providerId={bet.providerId}
              lockIfNoMarket={lock}
            />
          );
        });
    }
    return matchCardList;
  }

  function categoryName() {
    if (categoryId === 'all') return t('allcategories');
    const selectedLeagueObj = leagues.find((l) => l.leagueId === selectedLeague);
    if (!selectedLeagueObj) return '';
    return (
      categoryNames[selectedLeagueObj.events[0].categoryId] || selectedLeagueObj.events[0].category
    );
  }

  const handleScroll = () => {
    const container = window;
    const stickyEl = document.querySelector('.tip-types');
    if (!stickyEl) return;
    const scrollTop = container.scrollY;
    const direction = scrollTop < lastScrollPosition ? 'up' : 'down';
    setLastScrollPosition(scrollTop);

    if (scrollTop > 110) {
      stickyEl.classList.add('fixed');
      if (direction === 'down') {
        if (scrollTop > 200) {
          stickyEl.classList.remove('fixed');
        }
      } else {
        if (scrollTop > 200) {
          stickyEl.classList.add('fixed');
        }
      }
    } else {
      stickyEl.classList.remove('fixed');
    }
  };

  if (window) {
    window.removeEventListener('scroll', handleScroll);
    window.addEventListener('scroll', handleScroll);
  }

  if (loading) return <Loading message={t('search.loadingMatches')} />;

  const singleLineTitle = `${t('sport')} | ${t(`sportType.${sportType}`)}${categoryName() ? ` | ${categoryName()}` : ''}`;

  return (
    <>
      <div ref={containerRef} className="container scrollable-y">
        <div className="d-flex align-items-center py-2 gap-2">
          <button
            type="button"
            className="btn btn-link p-0 text-dark border-0 flex-shrink-0 me-1"
            onClick={() => navigate(-1)}
            aria-label="Back"
            style={{ flexShrink: 0 }}>
            <img
              width="18"
              height="25"
              src={`${AWS_BUCKET_URL}/Icons/backicon-caret.svg`}
              alt=""
              style={{ filter: 'brightness(0)' }}
            />
          </button>
          <div className="overflow-auto d-flex gap-2 flex-nowrap" style={{ minWidth: 0 }}>
            {hasOutrights && (
              <SportDetailCategory
                key="outrights"
                active={outrightsSelected}
                onClick={() => setOutrightsSelected(true)}
                text={t('outrights.label')}
              />
            )}
            {getLeagues()}
          </div>
        </div>
        <TitleHistoryBack title={singleLineTitle} subTitle={null} hideBackButton />
        {sportType === 'Football' && !outrightsSelected && (
          <Row className="">
            <Col className="tip-types-container">{getBetType()}</Col>
          </Row>
        )}
        <div className="">
          {outrightsSelected
            ? outrightEvents.map((event) => (
                <TournamentCard key={event.se?.id} event={event} />
              ))
            : getBets()}
        </div>
      </div>
    </>
  );
}
